import { Injectable, inject } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { combineLatest, map, Observable, of, catchError, forkJoin } from 'rxjs';
import { StateService } from '../shared/state.service';
import { Encounter, Condition, Appointment, ServiceRequest, Task } from 'fhir/r4';
import { AuthService } from '../shared/auth/auth.service';
import { UtilityService } from '../shared/utility.service';
import { inferServiceRequestType } from '../shared/service-request-type.util';
import { backendEndPointToken } from '../app.config';
import { LoadingUIEnabled, SkipHospitalSegregation } from '../loading.interceptor';

export interface ResourceWrapper<T> {
    referenceId: string | null;
    savedStatus: 'saved' | 'unsaved';
    actualResource: T;
}

export interface PendingDiagnosisSummary {
    count: number;
    firstPatientId: string | null;
    firstEncounterId: string | null;
}

export interface TodaysActivitySummary {
    outpatients: number;
    inpatients: number;
    virtualEncounters: number;
    surgeries: number;
}

export interface DashboardAppointmentSummary {
    appointmentId: string;
    patientReference: string;
    patientName: string;
    appointmentType: string;
    start?: string;
    end?: string;
}

export interface DashboardTaskSummary {
    taskId: string;
    title: string;
    patientReference: string;
    status: string;
    start?: string;
    end?: string;
}

@Injectable({
    providedIn: 'root'
})
export class DoctorDashboardService {
    private stateService = inject(StateService);
    private authService = inject(AuthService);
    private utilityService = inject(UtilityService);
    private backendUrl = inject(backendEndPointToken);

    /**
     * Current Patients: In-progress encounters assigned to the current practitioner.
     */
    getTodaysPatientsCount$(): Observable<number> {
        const practitionerId = this.authService.user.getValue()?.fhirResourceId;
        return this.stateService.orgWideResources.encounters.pipe(
            map((wrappers: ResourceWrapper<Encounter>[]) => {
                // alert(JSON.stringify(wrappers.map(w => JSON.stringify(w.actualResource.participant) + "-" + practitionerId)));
                return wrappers
                    .map(w => w.actualResource)
                    .filter(e => this.isActiveEncounterForPractitioner(e, practitionerId))
                    .filter(e => !this.isPharmacyEncounter(e))
                    .length;
            })
        );
    }

    getPendingDiagnosisSummary$(): Observable<PendingDiagnosisSummary> {
        const practitionerId = this.authService.user.getValue()?.fhirResourceId;
        const conditions$ = this.utilityService.threeTierQueryAndSyncToState$<Condition>({
            resourceType: 'Condition',
            scope: 'orgWide',
            tier1FollowNextLinks: true
        }).pipe(
            catchError(() => this.stateService.orgWideResources.condition.pipe(
                map((wrappers: ResourceWrapper<Condition>[]) => wrappers.map(w => w.actualResource))
            ))
        );

        return combineLatest([
            this.stateService.orgWideResources.encounters,
            conditions$
        ]).pipe(
            map(([encounterWrappers, conditions]: [
                ResourceWrapper<Encounter>[],
                Condition[]
            ]) => {
                // alert(practitionerId);
                const diagnosisEncounterIds = new Set(
                    (conditions ?? [])
                        .filter(condition => this.isUsableDiagnosisCondition(condition))
                        .map(condition => this.extractId(condition.encounter?.reference))
                        .filter((id): id is string => !!id)
                );

                const pendingEncounters = encounterWrappers
                    .map(w => w.actualResource)
                    .filter(encounter => this.isActiveEncounterForPractitioner(encounter, practitionerId))
                    .filter(encounter => !this.isPharmacyEncounter(encounter))
                    .filter(encounter => !!encounter.id && !diagnosisEncounterIds.has(encounter.id));

                return {
                    count: pendingEncounters.length,
                    firstPatientId: this.extractId(pendingEncounters[0]?.subject?.reference),
                    firstEncounterId: pendingEncounters[0]?.id ?? null
                };
            })
        );
    }

    getConsultationAppointmentsCount$(): Observable<number> {
        return this.getAppointmentsForCurrentPractitioner$().pipe(
            map((appointments) => (appointments ?? [])
                .filter((appointment) => this.isToday(appointment.start || appointment.requestedPeriod?.[0]?.start))
                .filter((appointment) => this.isConsultationAppointment(appointment))
                .length),
            catchError(() => of(0))
        );
    }

    getTelemedicineAppointmentsCount$(): Observable<number> {
        return this.getAppointmentsForCurrentPractitioner$().pipe(
            map((appointments) => (appointments ?? [])
                .filter((appointment) => this.isToday(appointment.start || appointment.requestedPeriod?.[0]?.start))
                .filter((appointment) => this.isTelemedicineAppointment(appointment))
                .length),
            catchError(() => of(0))
        );
    }

    getTodaysActivitySummary$(): Observable<TodaysActivitySummary> {
        const practitionerId = this.authService.user.getValue()?.fhirResourceId;
        const serviceRequests$ = this.utilityService.threeTierQueryAndSyncToState$<ServiceRequest>({
            resourceType: 'ServiceRequest',
            scope: 'orgWide',
            tier1FollowNextLinks: true
        }).pipe(
            catchError(() => this.stateService.orgWideResources.serviceRequests.pipe(
                map((wrappers: ResourceWrapper<any>[]) => wrappers.map(w => w.actualResource as ServiceRequest))
            ))
        );

        return combineLatest([
            this.stateService.orgWideResources.encounters,
            serviceRequests$
        ]).pipe(
            map(([encounterWrappers, serviceRequests]: [ResourceWrapper<Encounter>[], ServiceRequest[]]) => {
                const todaysEncounters = encounterWrappers
                    .map(w => w.actualResource)
                    .filter(encounter => this.isToday(encounter.period?.start))
                    .filter(encounter => this.isEncounterForPractitioner(encounter, practitionerId));

                const todaysSurgeryRequests = (serviceRequests ?? [])
                    .filter(serviceRequest => this.isToday(serviceRequest.authoredOn))
                    .filter(serviceRequest => this.isServiceRequestForPractitioner(serviceRequest, practitionerId))
                    .filter(serviceRequest => inferServiceRequestType(serviceRequest) === 'surgery');

                return {
                    outpatients: todaysEncounters.filter(encounter => this.isOutpatientEncounter(encounter)).length,
                    inpatients: todaysEncounters.filter(encounter => this.isInpatientEncounter(encounter)).length,
                    virtualEncounters: todaysEncounters.filter(encounter => this.isVirtualEncounter(encounter)).length,
                    surgeries: todaysSurgeryRequests.length
                };
            }),
            catchError(() => of({ outpatients: 0, inpatients: 0, virtualEncounters: 0, surgeries: 0 }))
        );
    }

    getNextAppointmentsForCurrentPractitioner$(limit = 3): Observable<DashboardAppointmentSummary[]> {
        return this.getAppointmentsForCurrentPractitioner$().pipe(
            map(appointments => (appointments ?? [])
                .map(appointment => this.toAppointmentSummary(appointment))
                .filter((appointment): appointment is DashboardAppointmentSummary => !!appointment && !!appointment.start)
                .filter(appointment => new Date(appointment.start as string).getTime() >= this.startOfToday().getTime())
                .sort((left, right) => new Date(left.start as string).getTime() - new Date(right.start as string).getTime())
                .slice(0, limit)),
            catchError(() => of([]))
        );
    }

    getNextTasksForCurrentUser$(limit = 3): Observable<DashboardTaskSummary[]> {
        const user = this.authService.user.getValue();
        const practitionerId = user?.fhirResourceId;
        if (!practitionerId) {
            return of([]);
        }

        const ownerRef = `Practitioner/${practitionerId}`;
        const directTasks$ = this.utilityService.threeTierQueryAndSyncToState$<Task>({
            resourceType: 'Task',
            urlSearchObject: { owner: ownerRef },
            querySearchObject: [{ key: 'owner.reference', value: [ownerRef] }],
            scope: 'orgWide',
            tier1FollowNextLinks: true
        }).pipe(catchError(() => of([] as Task[])));

        return forkJoin([directTasks$]).pipe(
            map(([directTasks]) => Array.from(new Map(
                directTasks
                    .filter(task => this.isOpenTask(task))
                    .map(task => [task.id || JSON.stringify(task), task])
            ).values())),
            map(tasks => tasks
                .map(task => this.toTaskSummary(task))
                .filter((task): task is DashboardTaskSummary => !!task)
                .sort((left, right) => this.taskSortTime(left) - this.taskSortTime(right))
                .slice(0, limit)),
            catchError(() => of([]))
        );
    }

    private getAppointmentsForCurrentPractitioner$(): Observable<Appointment[]> {
        const actorRef = this.getCurrentPractitionerReference();
        if (!actorRef) {
            return of([]);
        }

        return this.utilityService.threeTierQueryAndSyncToState$<Appointment>({
            resourceType: 'Appointment',
            urlSearchObject: { actor: actorRef },
            context: new HttpContext().set(LoadingUIEnabled, false).set(SkipHospitalSegregation, true),
            querySearchObject: [
                { key: 'participant.actor.reference', value: [actorRef, `${this.backendUrl}/${actorRef}`] }
            ],
            scope: 'orgWide',
            tier1FollowNextLinks: true
        });
    }

    private getCurrentPractitionerReference(): string {
        const raw = this.authService.user.getValue()?.fhirResourceId?.toString().trim() || '';
        if (!raw) {
            return '';
        }

        const noQuery = raw.split('?')[0];
        const marker = 'Practitioner/';
        const markerIndex = noQuery.indexOf(marker);
        if (markerIndex >= 0) {
            return noQuery.slice(markerIndex);
        }

        const id = noQuery.split('/').filter(Boolean).pop() || '';
        return id ? `Practitioner/${id}` : '';
    }

    private isTelemedicineAppointment(appointment: Appointment): boolean {
        return this.hasServiceCategory(appointment, 'telemedicine');
    }

    private isConsultationAppointment(appointment: Appointment): boolean {
        return this.hasServiceCategory(appointment, 'consultation');
    }

    private hasServiceCategory(appointment: Appointment, code: string): boolean {
        const target = code.toLowerCase();
        return appointment.serviceCategory?.some(category =>
            category.coding?.some(coding =>
                coding.code?.toLowerCase() === target ||
                coding.display?.toLowerCase() === target
            ) ||
            category.text?.toLowerCase() === target
        ) === true;
    }

    private isActiveEncounterForPractitioner(encounter: Encounter, practitionerId?: string): boolean {
        return encounter.status === 'in-progress' &&
            (!practitionerId || encounter.participant?.some(p => p.individual?.reference === `Practitioner/${practitionerId}`) === true);
    }

    private isEncounterForPractitioner(encounter: Encounter, practitionerId?: string): boolean {
        return !practitionerId ||
            !encounter.participant?.length ||
            encounter.participant.some(p => p.individual?.reference === `Practitioner/${practitionerId}`);
    }

    private isServiceRequestForPractitioner(serviceRequest: ServiceRequest, practitionerId?: string): boolean {
        return !practitionerId ||
            !serviceRequest.requester?.reference ||
            serviceRequest.requester.reference === `Practitioner/${practitionerId}`;
    }

    private isUsableDiagnosisCondition(condition: Condition): boolean {
        const verificationCodes = condition.verificationStatus?.coding
            ?.map(coding => coding.code?.toLowerCase())
            .filter((code): code is string => !!code) ?? [];

        return !verificationCodes.includes('entered-in-error');
    }

    private isPharmacyEncounter(encounter: Encounter): boolean {
        return encounter.type?.some(type => {
            const text = type.text?.toLowerCase() || '';
            return text.includes('pharmacy') ||
                type.coding?.some(coding =>
                    coding.code?.toUpperCase() === 'PHARM' ||
                    coding.display?.toLowerCase().includes('pharmacy')
                ) === true;
        }) === true;
    }

    private extractId(reference?: string): string | null {
        if (!reference) {
            return null;
        }

        return reference.split('/').pop() || null;
    }

    private toAppointmentSummary(appointment: Appointment): DashboardAppointmentSummary | null {
        const start = appointment.start || appointment.requestedPeriod?.[0]?.start;
        const patientReference = appointment.participant
            ?.map(participant => participant.actor)
            .find(actor => actor?.reference?.includes('Patient/'));

        return {
            appointmentId: appointment.id || '',
            patientReference: patientReference?.reference || '',
            patientName: patientReference?.display || this.extractId(patientReference?.reference) || 'Unknown Patient',
            appointmentType: this.getAppointmentServiceCategoryLabel(appointment) ||
                appointment.appointmentType?.coding?.[0]?.display ||
                appointment.appointmentType?.text ||
                appointment.serviceType?.[0]?.coding?.[0]?.display ||
                appointment.serviceType?.[0]?.text ||
                appointment.description ||
                'General',
            start,
            end: appointment.end || appointment.requestedPeriod?.[0]?.end
        };
    }

    private getAppointmentServiceCategoryLabel(appointment: Appointment): string {
        for (const category of appointment.serviceCategory || []) {
            for (const coding of category.coding || []) {
                const code = coding.code?.toLowerCase();
                if (code === 'telemedicine') return coding.display || category.text || 'Telemedicine';
                if (code === 'consultation') return coding.display || category.text || 'Consultation';
            }
            if (category.text) return category.text;
        }
        return '';
    }

    private toTaskSummary(task: Task): DashboardTaskSummary | null {
        return {
            taskId: task.id || '',
            title: task.description || task.code?.text || task.code?.coding?.[0]?.display || 'Task',
            patientReference: task.for?.reference || '',
            status: task.status || 'requested',
            start: task.restriction?.period?.start || task.authoredOn,
            end: task.restriction?.period?.end
        };
    }

    private isOpenTask(task: Task): boolean {
        const closed = new Set(['completed', 'cancelled', 'rejected', 'failed']);
        return !closed.has((task.status || '').toLowerCase());
    }

    private taskSortTime(task: DashboardTaskSummary): number {
        const value = task.start || task.end || '';
        const time = value ? new Date(value).getTime() : Number.MAX_SAFE_INTEGER;
        return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
    }

    private isInpatientEncounter(encounter: Encounter): boolean {
        const code = encounter.class?.code?.toUpperCase();
        const display = encounter.class?.display?.toLowerCase() || '';
        return code === 'IMP' || display.includes('inpatient');
    }

    private isOutpatientEncounter(encounter: Encounter): boolean {
        const code = encounter.class?.code?.toUpperCase();
        const display = encounter.class?.display?.toLowerCase() || '';
        return code === 'AMB' || code === 'SS' || display.includes('outpatient') || display.includes('ambulatory');
    }

    private isVirtualEncounter(encounter: Encounter): boolean {
        const code = encounter.class?.code?.toUpperCase();
        const display = encounter.class?.display?.toLowerCase() || '';
        const typeText = encounter.type?.map(type =>
            `${type.text || ''} ${type.coding?.map(coding => `${coding.code || ''} ${coding.display || ''}`).join(' ') || ''}`
        ).join(' ').toLowerCase() || '';
        return code === 'VR' ||
            display.includes('virtual') ||
            display.includes('tele') ||
            typeText.includes('virtual') ||
            typeText.includes('tele');
    }

    private startOfToday(): Date {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    private isToday(value?: string): boolean {
        if (!value) {
            return false;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return false;
        }

        return date.toDateString() === new Date().toDateString();
    }
}
